/**
 * Created by Andrey Popov on 06.08.26
 */

// Why the window can't skip players who already play the target game (Instant, zero permissions - investigated 01.09.2026):
// - FBInstant SDK 8.0 has no identity shared between two apps of one business: player.getID() and the ASID are
//   app-scoped, getSignedASIDAsync() is null for players without canvas/ToS history (all of ours), graphApi
//   /me/ids_for_business needs the permission model retired on 30.09.2026, getConnectedPlayersAsync() lists
//   friends playing THIS game only, and getAssociatedAppsASIDAsync() covers only apps linked in the Cross-play
//   tool - an irreversible link attesting they are "the same game" (a hasPlayedGame() built on it was removed).
// - Match The Cars (fb 1769176524477982) has no cross-promo at all; Candy Crush (King, fb 210831918949520)
//   resolves the player to a King account server-side and lets Braze pick promos - but on Instant it offers no
//   account login, so for zero-permissions players King can't link its games either.
// - Only a voluntary account entered by the player in both games could give that; not planned.
// So "already plays" here means the `connected` flag: the player left for the target through this window
// (switchGame; the platform callback fires only on failure and takes the flag back). A player who found the
// game on their own sees the window like everyone else, once per SESSIONS_BETWEEN_SHOWS sessions.

const GameAnnounce = {
    SESSIONS_BETWEEN_SHOWS: 5,

    getTarget: function () {
        return Object.keys(GameAnnounceConfig.TARGETS).find((target) => GameAnnounceConfig.TARGETS[target].sources.includes(cleverapps.config.name));
    },

    getBundleName: function () {
        return `game_announce_${GameAnnounce.getTarget()}`;
    },

    getLink: function () {
        const promotion = cleverapps.crossPromo && cleverapps.crossPromo.createPromotion(GameAnnounce.getTarget());
        return promotion && promotion.link;
    },

    getPayload: function () {
        const promotion = cleverapps.crossPromo && cleverapps.crossPromo.createPromotion(GameAnnounce.getTarget());
        const payload = promotion && promotion.payload || {};

        return {
            ...payload,
            promo: "game_announce",
            from: cleverapps.config.name,
            payerTier: cleverapps.paymentsHistory.classify()
        };
    },

    isAvailable: function () {
        if (!GameAnnounce.getTarget()) {
            return false;
        }

        if (!connector.platform.canSwitchGame()) {
            return false;
        }

        if (!cleverapps.user.checkAvailable(cleverapps.config.availables.GAME_ANNOUNCE)) {
            return false;
        }

        if (cleverapps.rewardedPromo && cleverapps.rewardedPromo.isAvailable() && cleverapps.rewardedPromo.isPromoting(GameAnnounce.getTarget())) {
            return false;
        }

        if (!GameAnnounce.getLink()) {
            return false;
        }

        GameAnnounce.logOnce("loggedAvailable", cleverapps.EVENTS.GAME_ANNOUNCE_AVAILABLE);

        return true;
    },

    canShow: function () {
        if (!GameAnnounce.isAvailable()) {
            return false;
        }

        const data = GameAnnounce.load();
        if (data.connected) {
            return false;
        }

        return data.shownAtSession === undefined || (data.sessions || 0) - data.shownAtSession >= GameAnnounce.SESSIONS_BETWEEN_SHOWS;
    },

    setShown: function () {
        const data = GameAnnounce.load();
        data.shown = Date.now();
        data.shownAtSession = data.sessions || 0;
        GameAnnounce.save(data);

        cleverapps.metrics.logEvent(`${cleverapps.EVENTS.GAME_ANNOUNCE_SHOWN}-${GameAnnounce.getTarget()}`);
    },

    logOnce: function (flag, event) {
        const data = GameAnnounce.load();
        if (data[flag]) {
            return;
        }

        data[flag] = true;
        GameAnnounce.save(data);

        cleverapps.metrics.logEvent(`${event}-${GameAnnounce.getTarget()}`);
    },

    // a successful switch leaves the game, so the platforms call back only when it failed
    // (the user dismissed the dialog, the target is unavailable) - the flag is set first
    // and taken back on that callback; platforms that just open a link never report
    switchGame: function () {
        const data = GameAnnounce.load();
        data.connected = true;
        GameAnnounce.save(data);

        connector.platform.switchGame(GameAnnounce.getLink(), GameAnnounce.getPayload(), () => {
            const failed = GameAnnounce.load();
            delete failed.connected;
            GameAnnounce.save(failed);
        });

        GameAnnounce.logOnce("loggedFirstLaunch", cleverapps.EVENTS.GAME_ANNOUNCE_FIRSTLAUNCH);
    },

    countSession: function () {
        const data = GameAnnounce.load();
        data.sessions = (data.sessions || 0) + 1;
        GameAnnounce.save(data);
    },

    load: function () {
        const data = cleverapps.dataLoader.load(DataLoader.TYPES.GAME_ANNOUNCE) || {};
        return data[GameAnnounce.getTarget()] || {};
    },

    save: function (data) {
        const all = cleverapps.dataLoader.load(DataLoader.TYPES.GAME_ANNOUNCE) || {};
        all[GameAnnounce.getTarget()] = data;
        cleverapps.dataLoader.save(DataLoader.TYPES.GAME_ANNOUNCE, all);
    }
};

cleverapps.InitByFeature["game_announce"] = function () {
    // the record was flat while ladacha was the only target
    const data = cleverapps.dataLoader.load(DataLoader.TYPES.GAME_ANNOUNCE);
    if (data && data.sessions !== undefined) {
        cleverapps.dataLoader.save(DataLoader.TYPES.GAME_ANNOUNCE, { ladacha: data });
    }

    // a real session (1 hour of inactivity, see connector Metrics.SESSION_TIMEOUT), not every page reload;
    // metrics start after InitByFeature, so the launch's own session is caught too
    connector.metrics.on("startSession", GameAnnounce.countSession);
};

Placements.ENTRIES.gameAnnounceWindow = {
    throttled: true,

    type: Placements.FREE_FOCUS_MAIN,

    delayFromStart: "2 minutes",

    filter: function () {
        return GameAnnounce.canShow();
    },

    action: function () {
        GameAnnounce.setShown();

        cleverapps.focusManager.display({
            focus: "GameAnnounceWindow",
            action: function (f) {
                new GameAnnounceWindow();
                cleverapps.focusManager.onceNoWindowsListener = f;
            }
        });
    }
};
